import { useMemo } from 'react';
import { VolSurfaceToken } from '../../../../../model/currency_type.enum';
import { OpenInterest, ChartDataItem, AggregatedData } from '../types';

interface UseProcessOpenInterestDataProps {
  data: OpenInterest | undefined;
  venue: string;
  expiry: string;
  token: VolSurfaceToken;
}

export const useProcessOpenInterestData = ({
  data,
  venue,
  expiry,
  token,
}: UseProcessOpenInterestDataProps) => {
  const chartData = useMemo((): ChartDataItem[] => {
    if (!data || !venue || !expiry || !data[venue]?.[token]) {
      return [];
    }

    const aggregatedData: AggregatedData = {};

    const processExpiryData = (dataByExpiry: any) => {
      const instrumentKeys = Object.keys(dataByExpiry);

      instrumentKeys.forEach((instrumentKey) => {
        const instrument = dataByExpiry[instrumentKey];
        const strike = instrument.strike;
        const openInterest = instrument.open_interest;
        const cp = instrument.cp;

        if (!aggregatedData[strike]) {
          aggregatedData[strike] = { call: 0, put: 0 };
        }

        if (cp === 'C') {
          aggregatedData[strike].call += openInterest;
        } else {
          aggregatedData[strike].put += openInterest;
        }
      });
    };

    if (expiry === 'all') {
      const expiries = Object.keys(data[venue][token]);
      expiries.forEach((exp) => {
        processExpiryData(data[venue][token][exp]);
      });
    } else if (data[venue][token][expiry]) {
      processExpiryData(data[venue][token][expiry]);
    }

    const result: ChartDataItem[] = [];

    Object.keys(aggregatedData).forEach((strike) => {
      if (aggregatedData[strike].call > 0) {
        result.push({
          key: `${strike}-C`,
          value: aggregatedData[strike].call,
          color: '#1FADAD',
          type: 'C',
        });
      }
      if (aggregatedData[strike].put > 0) {
        result.push({
          key: `${strike}-P`,
          value: aggregatedData[strike].put,
          color: '#F89007',
          type: 'P',
        });
      }
    });

    return result;
  }, [data, venue, expiry, token]);

  const xAxis = useMemo(() => {
    const strikes: Set<string> = new Set(chartData.map((item) => item.key.split('-')[0]));

    const data = Array.from(strikes).sort((a, b) => parseFloat(a) - parseFloat(b));

    return {
      type: 'category' as const,
      data,
      boundaryGap: true,
      axisTick: {
        alignWithLabel: true,
      },
      axisLabel: {
        align: 'center',
        rotate: 45,
        margin: 22,
      },
    };
  }, [data, chartData]);

  const yAxis = {
    type: 'value',
    position: 'right',
    axisPointer: { label: { margin: 10 } },
    axisLabel: {
      align: 'left',
      formatter: (value: number) => {
        if (value <= 1000) return value.toString();
        const kValue = value / 1000;
        const hasDecimal = kValue % 1 !== 0;

        return `${hasDecimal ? kValue.toFixed(1) : kValue}K`;
      },
    },
    min: 0,
  };

  const series = useMemo(() => {
    if (!chartData.length) return [];

    return [
      {
        name: 'Call',
        type: 'bar',
        color: '#1FADAD',
        data: chartData
          .filter((item) => item.type === 'C')
          .map((item) => ({
            value: item.value,
            name: item.key.split('-')[0],
          })),
        barWidth: '3.19px',
      },
      {
        name: 'Put',
        type: 'bar',
        color: '#F89007',
        data: chartData
          .filter((item) => item.type === 'P')
          .map((item) => ({
            value: item.value,
            name: item.key.split('-')[0],
          })),
        barWidth: '3.19px',
      },
    ];
  }, [chartData, venue, expiry, token]);

  return {
    data: chartData,
    xAxis,
    yAxis,
    series,
    hasData: chartData.length > 0,
  };
};
